import { useEffect, useState } from 'react';

import { Alert, Badge, Button, HelpTip, Input } from '@/components/ui';
import { api } from '@/lib/api';

import { formatTime } from '@/lib/format-date';
/**
 * 🔴 O TETO DO DIA — trava de verdade (Frente E, 08/09/2026).
 *
 * "ajuste o teto para 800 mensagens e trava a mensagem caso o teto seja
 * atingido": ao atingir, o envio PARA, o Matheus recebe e-mail na hora (um por
 * dia), e esta tela mostra **o que ficou sem sair e para quem** — mensagem
 * barrada que some em silêncio faria ele achar que o cliente foi avisado.
 *
 * ⚠️ "Liberar mais hoje" sobe a folga SÓ do dia (morre à meia-noite de
 * Brasília); o teto permanente se edita ao lado. O freio de emergência
 * continua a um clique, lá em cima — a trava não o substitui.
 */

interface Cfg {
  tetoDiario: number;
  tetoExtraHoje: number;
  tetoEfetivo: number;
  esperaEntreEnviosSeg: number;
  instabilidadeCarenciaMin: number;
  /** 🔴 A janela de silêncio (frente 12): 24h é o piso e o padrão. */
  janelaSilencioHoras: number;
  enviadasHoje: number;
}

interface Barrada {
  quandoIso: string;
  gatilho: string | null;
  clienteNome: string | null;
}

const AJUDA_TETO =
  'O máximo de mensagens que o IGM manda por dia, somando todos os avisos — atingiu, PAROU: nada '
  + 'mais sai até a meia-noite (horário de Brasília), você recebe um e-mail na hora e o que ficou '
  + 'sem sair aparece aqui embaixo. Existe para transformar uma configuração errada em "parou" em '
  + 'vez de "centenas de mensagens em grupos de cliente". Dá para liberar mais mensagens só hoje, '
  + 'sem mexer no teto de sempre.';

export function TetoDoDia({ cfg, onMudou }: { cfg: Cfg; onMudou: () => void }) {
  const [teto, setTeto] = useState(String(cfg.tetoDiario));
  const [espera, setEspera] = useState(String(cfg.esperaEntreEnviosSeg));
  const [carencia, setCarencia] = useState(String(cfg.instabilidadeCarenciaMin));
  const [janela, setJanela] = useState(String(cfg.janelaSilencioHoras));
  const [extra, setExtra] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [barradas, setBarradas] = useState<Barrada[]>([]);

  const estourou = cfg.enviadasHoje >= cfg.tetoEfetivo;

  useEffect(() => {
    setTeto(String(cfg.tetoDiario));
    setEspera(String(cfg.esperaEntreEnviosSeg));
    setCarencia(String(cfg.instabilidadeCarenciaMin));
    setJanela(String(cfg.janelaSilencioHoras));
  }, [
    cfg.tetoDiario,
    cfg.esperaEntreEnviosSeg,
    cfg.instabilidadeCarenciaMin,
    cfg.janelaSilencioHoras,
  ]);

  useEffect(() => {
    api
      .get<{ bloqueadas: Barrada[] }>('/api/v1/admin/whatsapp/bloqueadas-hoje')
      .then((r) => setBarradas(r.bloqueadas))
      .catch(() => setBarradas([]));
  }, [cfg.enviadasHoje]);

  async function chamar(caminho: string, corpo: unknown) {
    setErro(null);
    try {
      await api.post(caminho, corpo);
      onMudou();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    }
  }

  return (
    <div className="space-y-3 border-t border-line pt-4 dark:border-line-dark" data-testid="wa-teto">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">O teto do dia</span>
        <HelpTip texto={AJUDA_TETO} />
        <Badge
          variant={estourou ? 'danger' : 'neutral'}
          size="sm"
          data-testid="wa-teto-contador"
        >
          Hoje saíram {cfg.enviadasHoje} de {cfg.tetoEfetivo}
          {cfg.tetoExtraHoje > 0 ? ` (${cfg.tetoDiario} + ${cfg.tetoExtraHoje} liberadas hoje)` : ''}
        </Badge>
      </div>

      {erro && <Alert variant="danger">{erro}</Alert>}

      {estourou && (
        <Alert variant="danger" data-testid="wa-teto-estourado">
          <strong>O teto foi atingido — o envio parou.</strong> Nada mais sai hoje, a menos que você
          libere mais mensagens abaixo. Você recebeu (ou vai receber) um e-mail sobre isto.
        </Alert>
      )}

      {/* 🔴 O que ficou sem sair, e para quem — nunca em silêncio. */}
      {barradas.length > 0 && (
        <div className="rounded-lg border border-line p-3 text-sm dark:border-line-dark" data-testid="wa-barradas">
          <p className="font-medium text-ink-1 dark:text-ink-dark-1">
            {barradas.length === 1
              ? '1 mensagem barrada pelo teto hoje:'
              : `${barradas.length} mensagens barradas pelo teto hoje:`}
          </p>
          <ul className="mt-1 space-y-0.5 text-ink-2 dark:text-ink-dark-2">
            {barradas.slice(0, 10).map((b, i) => (
              <li key={i}>
                {formatTime(new Date(b.quandoIso))} —{' '}
                {b.clienteNome ?? 'cliente não identificado'}
                {b.gatilho ? ` · ${b.gatilho}` : ''}
              </li>
            ))}
            {barradas.length > 10 && <li>… e mais {barradas.length - 10}.</li>}
          </ul>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="text-xs text-ink-3 dark:text-ink-dark-3" htmlFor="wa-teto-diario">
            Teto por dia
          </label>
          <Input
            id="wa-teto-diario"
            type="number"
            value={teto}
            onChange={(e) => setTeto(e.target.value)}
            onBlur={() => {
              const v = Number(teto);
              if (Number.isFinite(v) && v !== cfg.tetoDiario) {
                void chamar('/api/v1/admin/whatsapp/ritmo', {
                  tetoDiario: v,
                  esperaSeg: Number(espera) || cfg.esperaEntreEnviosSeg,
                });
              }
            }}
            data-testid="wa-teto-diario"
          />
        </div>
        <div>
          <label className="text-xs text-ink-3 dark:text-ink-dark-3" htmlFor="wa-espera">
            <span className="inline-flex items-center gap-1">
              Espera entre envios (s)
              <HelpTip texto="A pausa entre uma mensagem e a seguinte no disparo em massa e nos avisos em sequência. Rajada é o que leva ao bloqueio do número." />
            </span>
          </label>
          <Input
            id="wa-espera"
            type="number"
            value={espera}
            onChange={(e) => setEspera(e.target.value)}
            onBlur={() => {
              const v = Number(espera);
              if (Number.isFinite(v) && v !== cfg.esperaEntreEnviosSeg) {
                void chamar('/api/v1/admin/whatsapp/ritmo', {
                  tetoDiario: Number(teto) || cfg.tetoDiario,
                  esperaSeg: v,
                });
              }
            }}
            data-testid="wa-espera"
          />
        </div>
        <div>
          <label className="text-xs text-ink-3 dark:text-ink-dark-3" htmlFor="wa-carencia">
            <span className="inline-flex items-center gap-1">
              Carência da instabilidade (min)
              <HelpTip texto="O intervalo mínimo entre avisos de instabilidade do MESMO equipamento. Um aparelho cujo sinal pisca não vira metralhadora de mensagens." />
            </span>
          </label>
          <Input
            id="wa-carencia"
            type="number"
            value={carencia}
            onChange={(e) => setCarencia(e.target.value)}
            onBlur={() => {
              const v = Number(carencia);
              if (Number.isFinite(v) && v !== cfg.instabilidadeCarenciaMin) {
                void chamar('/api/v1/admin/whatsapp/ritmo', {
                  tetoDiario: Number(teto) || cfg.tetoDiario,
                  esperaSeg: Number(espera) || cfg.esperaEntreEnviosSeg,
                  carenciaInstabilidadeMin: v,
                });
              }
            }}
            data-testid="wa-carencia"
          />
        </div>
        {/* ─── 🔴 A JANELA DE SILÊNCIO (frente 12) ───
            O adendo é literal sobre o que a tela tem de dizer ANTES de salvar:
            "enquanto o problema durar, o cliente recebe um aviso a cada 24
            horas" — e quantas mensagens isso daria numa semana. O remédio,
            se incomodar, é este campo; não é pedir código. */}
        <div>
          <label className="text-xs text-ink-3 dark:text-ink-dark-3" htmlFor="wa-janela">
            <span className="inline-flex items-center gap-1">
              Silêncio sobre o mesmo problema (h)
              <HelpTip texto="O mesmo equipamento do mesmo cliente não gera duas mensagens dentro deste intervalo. Um equipamento que o cliente ainda não conhecia rompe o silêncio e avisa na hora — o que se cala é a repetição, não a novidade. Mínimo de 24 horas." />
            </span>
          </label>
          <Input
            id="wa-janela"
            type="number"
            min={24}
            value={janela}
            onChange={(e) => setJanela(e.target.value)}
            onBlur={() => {
              const v = Number(janela);
              if (Number.isFinite(v) && v !== cfg.janelaSilencioHoras) {
                void chamar('/api/v1/admin/whatsapp/ritmo', {
                  tetoDiario: Number(teto) || cfg.tetoDiario,
                  esperaSeg: Number(espera) || cfg.esperaEntreEnviosSeg,
                  janelaSilencioHoras: v,
                });
              }
            }}
            data-testid="wa-janela"
          />
          <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3" data-testid="wa-janela-conta">
            Enquanto o problema durar, o cliente recebe um aviso a cada{' '}
            {Math.max(24, Number(janela) || 24)} horas — cerca de{' '}
            <b>{Math.max(1, Math.floor((7 * 24) / Math.max(24, Number(janela) || 24)))}</b> numa
            semana, por equipamento.
          </p>
        </div>
        <div>
          <label className="text-xs text-ink-3 dark:text-ink-dark-3" htmlFor="wa-teto-extra">
            <span className="inline-flex items-center gap-1">
              Liberar mais HOJE
              <HelpTip texto="Sobe a folga só de hoje — à meia-noite (Brasília) ela morre sozinha. O teto de sempre fica como está. Antes de liberar, vale olhar a lista do que ficou sem sair: se o volume não era esperado, o problema é a configuração, não o teto." />
            </span>
          </label>
          <div className="flex gap-2">
            <Input
              id="wa-teto-extra"
              type="number"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder={cfg.tetoExtraHoje > 0 ? String(cfg.tetoExtraHoje) : '0'}
              data-testid="wa-teto-extra"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const v = Number(extra);
                if (Number.isFinite(v) && v >= 0) {
                  setExtra('');
                  void chamar('/api/v1/admin/whatsapp/teto-extra', { quantidade: v });
                }
              }}
              data-testid="wa-liberar-teto"
            >
              Liberar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
